import React from "react";
import {
  Alert,
  AlertTitle,
  CircularProgress,
  Typography,
  Box,
  Skeleton,
  Grid,
} from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";

const placeholderFields = new Array(3).fill({
  fieldId: "",
  position: { xs: 12 },
});

const FormSkeleton = ({ fields = [], formName }) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
      </Grid>

      {(fields.length ? fields : placeholderFields).map((field, index) => (
        <Grid
          item
          key={field?.fieldId || `placeholder-${index}`}
          {...(field?.position || { xs: 12 })}
        >
          <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={40} />
        </Grid>
      ))}
    </Grid>
  );
};

const WithLoadingError = ({
  data,
  isLoading,
  error,
  children,
  loadingHeight,
  useSkeleton,
}) => {
  const skeletonFields = data?.data?.formFields ?? [];
  const formTitle = data?.data?.formName || "Default Form Title";

  if (isLoading) {
    return useSkeleton ? (
      <Box width="100%">
        <FormSkeleton fields={skeletonFields} formName={formTitle} />
      </Box>
    ) : (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height={loadingHeight}
      >
        <CircularProgress />
        <Typography variant="body2" mt={1}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box my={2} mx="auto" maxWidth="500px">
        <Alert severity="error" icon={<ErrorOutline fontSize="inherit" />}>
          <AlertTitle>Error</AlertTitle>
          {typeof error === "string"
            ? error
            : error?.message || "An unexpected error occurred"}
        </Alert>
      </Box>
    );
  }

  // 🚀 Show Skeleton instead of "No data available"
  if (
    !data ||
    (Array.isArray(data) && data.length === 0) ||
    Object.keys(data).length === 0
  ) {
    return (
      <Box width="100%">
        <FormSkeleton fields={skeletonFields} formName={formTitle} />
      </Box>
    );
  }

  return <Box width="100%">{children}</Box>;
};

WithLoadingError.defaultProps = {
  data: null,
  isLoading: false,
  error: null,
  loadingHeight: "100%",
  useSkeleton: true,
};

export default WithLoadingError;
